/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, FormHelperText, TextField } from '@mui/material';
import { LuiSelect, LuiToggleButtonGroup } from '@jda/lui-common-component-library-mui5';

import { useStyles } from '../../../shared/PolicyDialog/styles';
import { selectError } from '../../../store/error/errorSelector';
import { compareByProperty } from '../../../utility/commonUtility';
import {
  DISPLAY_NAME,
  DROPDOWN_MAX_HEIGHT,
  ID,
  MEASURES,
  MEASURES_GROUP_LABEL,
  MEASURE_GROUP,
  POLICY_ACTIONS_LABEL,
  POLICY_ACTION_HELPER_TEXT,
  POLICY_DESC_LABEL,
  POLICY_NAME_LABEL,
  SELECT_ALL
} from '../../../shared/PolicyDialog/constants';
import { MeasuresListSelecter } from './MeasuresListSelecter';
import { ISelect } from '../../../models/Roles/RolesContracts';
import { IMeasureGroup, IMeasures } from '../../../models/Policies/PolicyContract';
import { ErrorMessage } from '../../../shared/ErrorMessage/ErrorMessage';
import { fetchDataPlatformActions } from '../../../store/role/policy';
import { getAllMeasureGroup, getAllMeasures } from '../../../store/role/PolicyMeasure/measure';
import { selectMeasuresGroupPolicyActions, selectMeasuresPolicyActions } from '../../../store/role/roleSelector';
import { setMeasuresPolicyFormData, updateMeasuresPolicyFormError } from '../../../store/role/PolicyMeasure/measureActionCreator';
import { selectMeasureGroup, selectMeasures, selectMeasuresFormData, selectMeasuresFormError } from '../../../store/role/PolicyMeasure/measureSelector';
import LuiMultiSelectCustom from '../../../shared/LuiMultiSelectCustom';

const buttonGroupMeasurePolicyType = [
  {
    id: 1,
    value: MEASURE_GROUP,
  },
  {
    id: 2,
    value: MEASURES,
  },
];

const Measure = (props: any) => {
  const { setIsSaveDisabled } = props;
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const error = useSelector(selectError);
  const measuresPolicyActions = useSelector(selectMeasuresPolicyActions);
  const measuresGroupPolicyActions = useSelector(selectMeasuresGroupPolicyActions);
  const policyData = useSelector(selectMeasuresFormData);
  const selectedAllMeasures: IMeasures[] = useSelector(selectMeasures);
  const formErrorState = useSelector(selectMeasuresFormError);
  const selectedAllMeasureGroup = useSelector(selectMeasureGroup);

  const measureData = useMemo(() => {
    const measuresUniqueByGroup = _.uniqBy(selectedAllMeasures, measure => measure.measureGroup);

    return measuresUniqueByGroup.map(measure => ({
      ...measure,
      key: measure.measureGroup,
      displayName: measure.measureGroup
    }));
  }, [selectedAllMeasures]);

  const [selectedActions, setSelectedActions] = useState<any>([]);
  const [selectedMeasureGroup, setSelectedMeasureGroup] = useState<any>([]);
  const [measuresOfMeasureGroup, setmeasuresOfMeasureGroup] = useState<any>([]);
  const [selectedMeasurePolicyType, setSelectedMeasurePolicyType] = useState<any>(0);

  useEffect(() => {
    fetchDataPlatformActions(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (selectedMeasurePolicyType) {
      getAllMeasures(dispatch);
    } else {
      getAllMeasureGroup(dispatch);
    }
  }, [dispatch, selectedMeasurePolicyType]);

  const handleMeasureSaveButtonDisable = useCallback(() => {
    if (
      policyData.policyName !== '' &&
      policyData.policyDescription !== '' &&
      (policyData.isMeasureGroup ?
        policyData.policyMeasureGroup !== ''
        : policyData.policyMeasures !== ''
      ) &&
      policyData.policyAction.length !== 0
    ) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }
  }, [policyData, setIsSaveDisabled]);

  useEffect(() => {
    handleMeasureSaveButtonDisable();
  }, [handleMeasureSaveButtonDisable]);

  const handleButtonChange = (buttonItem: any, buttonIndex: number | number[]) => {
    dispatch(setMeasuresPolicyFormData('isMeasureGroup', buttonItem.value === MEASURE_GROUP ? true : false));
    setSelectedMeasurePolicyType(buttonIndex);
    dispatch(setMeasuresPolicyFormData('policyAction', []));
  };

  const handleMultiSelectChange = (name: string, event: ISelect[]) => {
    if (name === "policyMeasureGroup") {
      setSelectedMeasureGroup(event);
    } else {
      setSelectedActions(event);
    }
    dispatch(setMeasuresPolicyFormData(name, event));
    dispatch(updateMeasuresPolicyFormError(name, ''));
  };

  const handleMeasureGroupSelectChange = (event: any) => {
    // if measure policy is selected then render measures select measures of 
    // selected measure group
    dispatch(setMeasuresPolicyFormData('policyMeasureGroup', event.target.value.measureGroup))
    if (event.target.value && event.target.value.measureGroup) {
      const filteredMeasures = _.filter(selectedAllMeasures, (measure: IMeasures) => {
        if (event.target.value.measureGroup === measure.measureGroup) return true;
      });
      setmeasuresOfMeasureGroup(filteredMeasures);
    }
  };

  const onChangeMeasureList = (items: any[]) => {
    if (items.length) {
      dispatch(setMeasuresPolicyFormData('policyMeasures', items));
    }
  };

  const handleTextFieldOnChange = (e: { target: { name: string; value: any } }) => {
    dispatch(setMeasuresPolicyFormData(e.target.name, e.target.value));
    dispatch(updateMeasuresPolicyFormError(e.target.name, ''));
  };

  const updateSelectedItemsWithOption = (data: IMeasureGroup[]) => {
    return data.map(item =>
      ({ ...item, displayName: item.id }))?.sort(compareByProperty(ID)) || [];
  };

  return (
    <>
      <Grid container>
        <Grid item xs={6} className={classes.formStyle}>
          <TextField
            id="policyName"
            name="policyName"
            label={POLICY_NAME_LABEL}
            variant="standard"
            className={classes.textFieldSpacing}
            fullWidth
            value={policyData?.name}
            onChange={handleTextFieldOnChange}
            error={formErrorState.error && formErrorState.errorPolicyNameHelperText !== ''}
            helperText={formErrorState.errorPolicyNameHelperText}
          />
        </Grid>
        <Grid item xs={6} className={classes.formStyle}>
          <TextField
            id="policyDescription"
            name="policyDescription"
            label={POLICY_DESC_LABEL}
            className={classes.textFieldSpacing}
            value={policyData?.description}
            onChange={handleTextFieldOnChange}
            fullWidth
            variant="standard"
            error={formErrorState.error && formErrorState.errorPolicyDescriptionHelperText !== ''}
            helperText={formErrorState.errorPolicyDescriptionHelperText}
          />
        </Grid>
        <Grid item xs={6} className={classes.formStyle}>
          <LuiToggleButtonGroup
            activeButtonIndex={0}
            onButtonChange={handleButtonChange}
            buttonGroupData={buttonGroupMeasurePolicyType}
          />
        </Grid>
      </Grid>
      {!selectedMeasurePolicyType ?
        <Grid item xs={6} className={classes.formStyle}>
          <LuiMultiSelectCustom
            id="measure-group-multi-select"
            itemData={updateSelectedItemsWithOption(selectedAllMeasureGroup)}
            itemKey="id"
            itemDisplayName="displayName"
            inputLabel={MEASURES_GROUP_LABEL}
            selectedItems={selectedMeasureGroup}
            selectAll
            selectAllDisplayText={SELECT_ALL}
            maxHeight={DROPDOWN_MAX_HEIGHT}
            onSelectionChange={(e: any) => handleMultiSelectChange('policyMeasureGroup', e)}
          />
          <FormHelperText>{POLICY_ACTION_HELPER_TEXT}</FormHelperText>
          <FormHelperText
            id="measuregroup-helper-text"
            error={formErrorState.policyMeasureGroup !== ''}
          >
            {formErrorState.policyMeasureGroup}
          </FormHelperText>
        </Grid> :
        <>
          <Grid item xs={12} className={classes.formStyle}>
            <LuiSelect
              width={380}
              id="measure-group-dropdown"
              maxHeight={DROPDOWN_MAX_HEIGHT}
              inputLabel={MEASURES_GROUP_LABEL}
              onSelectionChange={handleMeasureGroupSelectChange}
              itemData={measureData}
            />
            <FormHelperText
              id="measuregroup-helper-text"
              error={formErrorState.policyMeasureGroup !== ''}
            >
              {formErrorState.policyMeasureGroup}
            </FormHelperText>
          </Grid>
          <MeasuresListSelecter
            onChange={onChangeMeasureList}
            measuresArray={measuresOfMeasureGroup}
          />
        </>
      }

      <Grid item xs={6} className={classes.formStyle}>
        <LuiMultiSelectCustom
          id="actions-multi-select"
          itemData={selectedMeasurePolicyType ?
            measuresPolicyActions?.sort(compareByProperty(DISPLAY_NAME)) :
            measuresGroupPolicyActions?.sort(compareByProperty(DISPLAY_NAME))
          }
          itemKey="name"
          itemDisplayName="displayName"
          inputLabel={POLICY_ACTIONS_LABEL}
          selectedItems={selectedActions}
          selectAll
          selectAllDisplayText={SELECT_ALL}
          maxHeight={DROPDOWN_MAX_HEIGHT}
          onSelectionChange={(e: any) => handleMultiSelectChange('policyAction', e)}
        />
        <FormHelperText>{POLICY_ACTION_HELPER_TEXT}</FormHelperText>
        <FormHelperText
          id="policyAction-helper-text"
          error={formErrorState.policyAction !== ''}
        >
          {formErrorState.policyAction}
        </FormHelperText>
      </Grid>

      <Grid item xs={12} className={classes.formErrorStyle}>
        {error && <ErrorMessage {...error} />}
      </Grid>
    </>
  );
};

export default Measure;
